import {
    faBed,
    faBookAtlas,
    faChild,
    faFileInvoiceDollar,
    faHashtag,
    faMoneyBill,
    faMoon,
    faRightFromBracket,
    faRightToBracket,
    faUser,
    faUserFriends
} from "@fortawesome/free-solid-svg-icons";
import { AppRoutes } from "config/router";
import { Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import { gql } from "@apollo/client";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import {
    BookingDatalistQuery,
    BookingDatalistQueryVariables,
    BookingFilterInput,
    BookingSortInput,
    SortEnumType
} from "types/graphql";
import { graph } from "config/axios-context";
import { uuid } from "config/app-constants";
import { DatalistColumnDefinitions, getBottomClassName } from "utilities/datalist/datalist-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import MoneyView from "components/common/views/MoneyView";
import { appendFilters, Conditions } from "utilities/datalist/datalist-filter-utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RoomTypeView from "components/entities/accommodation/roomType/RoomTypeView";
import BookingStatusView from "components/entities/accommodation/booking/views/BookingStatusView";
import * as lodash from "lodash";
import BookingTotalCharge from "./views/BookingTotalCharge";
import { DropdownInputPropsType } from "../../../../types/form-types";
import DropdownInput from "../../../common/form/inputs/dropdown/DropdownInput";
import { PartialDeep } from "type-fest";
import { useState } from "react";
import HTMLCode from "components/common/HTMLCode";
import RoomTypeDropdownInput from "../roomType/RoomTypeDropdownInput";


export type BookingDatalistPropsType = {
    config?: PartialDeep<DatalistPropsType<ListItem, BookingFilterInput, BookingSortInput>>
    name: string,
    fixedFilters?: BookingFilterInput,
    hasBottomNavigation: boolean,
    hideTotalRow?: boolean,
    hideColumns?: ('id' | 'inSeasonId' | 'bookingDate' | 'inDate' | 'outDate' | 'nights' | 'person' | 'guestCount' | 'children' | 'roomType' | 'pricePerNight' | 'room' | 'total' | 'paidAmount' | 'isCancelled' | 'channel' | 'season')[]
}

type ListItem = NonNullable<BookingDatalistQuery["bookings"]>["items"][0];

type FooterData = {
    totalSum: number,
    totalAverage: number,
    guestCountAverage: number,
    guestCountSum: number,
    nightsAverage: number,
    nightsSum: number
    channelTotalSum: number,
    channelTotalAverage: number
}

const query = gql`
query BookingDatalist(
  $where: BookingFilterInput
  $order: [BookingSortInput!]
  $skip: Int
  $take: Int
  $channelWhere: BookingBookingChannelFilterInput
) {
  bookings {
    count(where: $where)
    items(where: $where, skip: $skip, order: $order, take: $take) {
      id
      person {
        name,
      }
      inDate
      outDate
      nights
      isCancelled
      hasConflict
      bookingDate
      children
      guestCount
      inSeasonId
      total
      paidAmount
      roomType {
        color
        name
        id
      }
      channel {
        channel {
          name
        }
        total
        isCancelled
      }
      season {
        name
      }
      details {
        items {
          price
          room {
            name
            roomType {
              color
              name
              id
            }
          }
        }
        priceSum
      }
      additionalCharge {
        amountSum
        count
      }
    }
    totalSum(where: $where)
    totalAverage(where: $where)
    guestCountAverage(where: $where)
    guestCountSum(where: $where)
    nightsAverage(where: $where)
    nightsSum(where: $where)
  }
  bookingBookingChannels {
    totalSum(where: $channelWhere)
    totalAverage(where: $channelWhere)
  }
}
`;

type StateFilterTypes = 'all' | 'gone' | 'staing' | 'future' | 'staingAndFuture'

const BookingDatalist = (props: BookingDatalistPropsType) => {
    const context = useAppContext();
    const [footerData, setFooterData] = useState<FooterData>({
        totalSum: 0,
        totalAverage: 0,
        guestCountAverage: 0,
        guestCountSum: 0,
        nightsAverage: 0,
        nightsSum: 0,
        channelTotalSum: 0,
        channelTotalAverage: 0
    });

    const stateFilterDropdownConfig: DropdownInputPropsType<StateFilterTypes> = {
        dataSource: {
            data: [
                'all',
                'gone',
                'staing',
                'future',
                'staingAndFuture'
            ]
        },
        name: 'bookingDatalistStateFilters',
        getItemId: i => i,
        placeholder: 'Select state',
        view: {
            inputView: i => i ? context.t('accommodation.booking.statusFilters.' + i) : ''
        }
    }

    const datalistDefaultConfig: DatalistPropsType<ListItem, BookingFilterInput, BookingSortInput> = {
        itemId: i => i.id,
        name: 'bookingDatalistPage',
        dataSource: {
            request: async params => {
                try {
                    let variables: BookingDatalistQueryVariables = {
                        where: params.filters,
                        order: params.sort,
                        skip: params.skip,
                        take: params.take
                    }

                    if (params.searchText) {
                        const search: BookingFilterInput = {
                            or: [
                                {
                                    person: {
                                        name: {
                                            contains: params.searchText
                                        }
                                    }
                                },
                                {
                                    person: {
                                        normalizedName: {
                                            contains: params.searchText
                                        }
                                    }
                                },
                                {
                                    or: [
                                        {
                                            person: {
                                                contactInfo: {
                                                    some: {
                                                        value: {
                                                            contains: params.searchText
                                                        }
                                                    }
                                                }
                                            }
                                        },
                                        {
                                            person: {
                                                contactInfo: {
                                                    some: {
                                                        value: {
                                                            contains: params.searchText.replace(/\s/g, '')
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    ]
                                },
                                {
                                    person: {
                                        tags: {
                                            some: {
                                                tag: {
                                                    name: {
                                                        contains: params.searchText
                                                    }
                                                }
                                            }
                                        }
                                    }
                                },
                                {
                                    person: {
                                        tags: {
                                            some: {
                                                tag: {
                                                    normalizedName: {
                                                        contains: params.searchText
                                                    }
                                                }
                                            }
                                        }
                                    }
                                },
                                {
                                    channel: {
                                        externalReference: {
                                            contains: params.searchText
                                        }
                                    }
                                },
                                {
                                    channel: {
                                        channel: {
                                            name: {
                                                contains: params.searchText
                                            }
                                        }
                                    }
                                },
                                {
                                    channel: {
                                        channel: {
                                            normalizedName: {
                                                contains: params.searchText
                                            }
                                        }
                                    }
                                }
                            ]
                        };

                        variables.where = appendFilters(variables.where, search);
                    }

                    if (props.fixedFilters) {
                        variables.where = appendFilters(variables.where, props.fixedFilters);
                    }

                    if (variables.where) {
                        variables.channelWhere = {
                            booking: variables.where
                        }
                    }

                    const response = await graph<BookingDatalistQuery, BookingDatalistQueryVariables>({
                        query: query,
                        variables: variables
                    });

                    if (response.errors) {
                        throw response.errors;
                    }

                    setFooterData({
                        totalSum: response.data!.bookings!.totalSum,
                        totalAverage: response.data!.bookings!.totalAverage,
                        guestCountAverage: response.data!.bookings!.guestCountAverage,
                        guestCountSum: response.data!.bookings!.guestCountSum,
                        nightsAverage: response.data!.bookings!.nightsAverage,
                        nightsSum: response.data!.bookings!.nightsSum,
                        channelTotalSum: response.data!.bookingBookingChannels!.totalSum,
                        channelTotalAverage: response.data!.bookingBookingChannels!.totalAverage
                    });

                    return response.data!.bookings!;
                } catch (error: any) {
                    context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            sort: [
                {
                    name: 'inDate',
                    direction: SortEnumType.Asc,
                    id: uuid()
                },
                {
                    name: 'outDate',
                    direction: SortEnumType.Asc,
                    id: uuid()
                }
            ]
        },
        table: {
            columns: [
                { // id
                    name: 'id',
                    title: context.t('fields.id.name'),
                    type: 'number',
                    sort: true,
                    hide: true
                },
                { // inSeasonId
                    name: 'inSeasonId',
                    title: 'fields.inSeasonId.shortName',
                    type: 'number',
                    hide: props.hideColumns?.includes('inSeasonId'),
                    sort: {
                        title: context.t('fields.inSeasonId.name'),
                        resolve: direction => {
                            return {
                                inSeasonId: direction
                            };
                        }
                    },
                    card: {
                        group: 'reference',
                        icon: faHashtag,
                        textSize: 'text-sm',
                        groupOrder: 1
                    },
                },
                { // bookingDate
                    name: 'bookingDate',
                    title: 'fields.bookingDate.name',
                    type: 'date',
                    hide: true,
                    sort: true,
                    filter: {
                        type: 'date'
                    }
                },
                { // inDate
                    name: 'inDate',
                    title: 'fields.inDate.shortName',
                    type: 'dateOnly',
                    hide: props.hideColumns?.includes('inDate'),
                    sort: true,
                    filter: {
                        type: 'dateOnly'
                    },
                    card: {
                        group: 'date',
                        // icon: faCalendar,
                        resolve: i => <span><FontAwesomeIcon icon={faRightToBracket}
                            className="rotate-90" /> {DateUtil.format(i.inDate, DateFormats.SHORT)}</span>,
                    }
                },
                { // outDate
                    name: 'outDate',
                    title: "fields.outDate.shortName",
                    type: 'dateOnly',
                    hide: props.hideColumns?.includes('outDate'),
                    sort: true,
                    filter: {
                        type: 'dateOnly'
                    },
                    card: {
                        resolve: i => <span><FontAwesomeIcon icon={faRightFromBracket}
                            className="-rotate-90" /> {DateUtil.format(i.outDate, DateFormats.SHORT)}</span>,
                        group: 'date',
                        // icon: faCalendar,
                    }
                },
                { // nights
                    name: 'nights',
                    title: context.t('fields.nights.name'),
                    type: 'number',
                    hide: props.hideColumns?.includes('nights'),
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        icon: faMoon,
                        group: 'date',
                    }
                },
                { // peson
                    ...DatalistColumnDefinitions.personColumnDefinition(context),
                    hide: props.hideColumns?.includes('person'),
                    card: {
                        icon: faUser,
                        group: 'person',
                    }
                },
                { // guestCount
                    name: 'guestCount',
                    title: 'fields.guest-count.shortName',
                    type: 'number',
                    hide: props.hideColumns?.includes('guestCount'),
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        icon: faUserFriends,
                        group: 'person'
                    }
                },
                { // children
                    name: 'children',
                    title: 'fields.children.shortName',
                    type: 'string',
                    hide: props.hideColumns?.includes('children'),
                    sort: true,
                    filter: {
                        type: 'string'
                    },
                    card: {
                        icon: faChild,
                        group: 'person',
                    }
                },
                { // roomType
                    name: 'roomType',
                    title: "fields.room.shortName",
                    hide: props.hideColumns?.includes('roomType'),
                    type: 'string',
                    resolve: i => <span>
                        <RoomTypeView name={i.roomType.name[0]} color={i.roomType.color} />
                        <HTMLCode />
                        ({lodash.uniq(i.details.items.map(x => x.room.name)).map((x, index) => {
                            const detail = i.details.items.find(y => y.room.name === x)!;
                            return <RoomTypeView key={`room-detail-${i.id}-${index}`} name={detail.room.name}
                                color={detail.room.roomType.color} />
                        })})
                    </span>,
                    sort: {
                        resolve: d => ({
                            roomType: {
                                name: d
                            }
                        })
                    },
                    filter: {
                        type: 'reference',
                        title: 'fields.roomType.name',
                        resolve: i => {
                            if (i.condition === Conditions.eq) {
                                return {
                                    roomType: {
                                        id: {
                                            [i.condition]: i.value.id
                                        }
                                    }
                                }
                            }

                            return {
                                roomType: {
                                    name: {
                                        [i.condition!]: i.value
                                    }
                                }
                            }
                        },
                        valueJSX: [
                            {
                                name: `${props.name}-roomTypeFilterEquals`,
                                conditionType: [Conditions.eq],
                                renderInput: (props, onChange) => {
                                    return <RoomTypeDropdownInput
                                        label={false}
                                        name={`${props.name}-roomtype-filter-equals-input`}
                                        value={props.value?.value as any}
                                        onChange={onChange}
                                    />
                                }
                            }
                        ]
                    },
                    card: {
                        icon: faBed,
                        group: 'price'
                    }
                },
                { // pricePerNight
                    name: 'pricePerNight',
                    title: 'fields.pricePerNight.shortName',
                    hide: props.hideColumns?.includes('pricePerNight'),
                    type: 'number',
                    resolve: i => <MoneyView value={lodash.uniq(i.details.items.map(x => (x.price))).join('|')} />,
                    card: {
                        group: 'price',
                        icon: faMoneyBill,
                    },
                    filter: {
                        type: 'number',
                        resolve: i => {
                            return {
                                details: {
                                    some: {
                                        price: {
                                            [i.condition!]: i.value
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                { // room
                    name: 'room',
                    title: "fields.room.shortName",
                    type: 'string',
                    hide: true,
                    filter: {
                        type: 'reference',
                        title: 'fields.room.name',
                        resolve: i => {
                            return {
                                details: {
                                    some: {
                                        room: {
                                            name: {
                                                [i.condition!]: i.value
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    },
                    card: {
                        icon: faBed,
                        group: 'price'
                    }
                },
                { // total
                    name: 'total',
                    title: context.t('fields.total.name'),
                    type: 'number',
                    hide: props.hideColumns?.includes('total'),
                    sort: true,
                    resolve: i => <BookingTotalCharge total={i.total} additionalCharge={i.additionalCharge.amountSum}
                        paid={i.paidAmount} />,
                    filter: {
                        type: 'number'
                    },
                    card: {
                        group: 'totalPrice',
                    }
                },
                { // paidAmount
                    name: 'paidAmount',
                    title: context.t('fields.paidAmount.name'),
                    type: 'number',
                    hide: true,
                    sort: true,
                    filter: {
                        type: 'nullNumber'
                    }
                },
                { // isCancelled
                    name: 'isCancelled',
                    title: context.t('fields.state.name'),
                    type: 'boolean',
                    hide: props.hideColumns?.includes('isCancelled'),
                    sort: {
                        title: context.t('fields.isCancelled.name'),
                        resolve: d => ({
                            isCancelled: d
                        })
                    },
                    resolve: i => <BookingStatusView addText hasConflict={i.hasConflict} isCancelled={i.isCancelled}
                        inDate={i.inDate}
                        outDate={i.outDate} />,
                    columnClassName: () => 'text-left',
                    filter: {
                        title: context.t('fields.isCancelled.name'),
                        type: 'boolean',

                    },
                    card: {
                        group: 'price',
                        resolve: i => <BookingStatusView hasConflict={i.hasConflict} isCancelled={i.isCancelled}
                            inDate={i.inDate}
                            outDate={i.outDate} />,
                    }
                },
                { //channel
                    name: 'channel',
                    hide: props.hideColumns?.includes('channel'),
                    title: 'fields.channel.shortName',
                    resolve: i => {
                        if (!i.channel) return <></>;
                        return !i.channel.isCancelled ? <>{i.channel.channel.name} </> :
                            <del className="text-neutral-500">{i.channel.channel.name}</del>;
                    },
                    sort: {
                        resolve: d => ({
                            channel: {
                                channel: {
                                    name: d
                                }
                            }
                        }),
                    },
                    filter: {
                        type: 'reference'
                    },
                    card: {
                        icon: faBookAtlas,
                        group: 'reference',
                        textSize: 'text-sm',
                    }
                },
                { // season
                    ...DatalistColumnDefinitions.seasonColumnDefinition(context, 'reference', true, {
                        textSize: 'text-sm',
                    }),
                    hide: props.hideColumns?.includes('season'),
                },
                { // status
                    name: 'status',
                    hide: true,
                    resolve: () => '',
                    filter: {
                        title: 'accommodation.booking.state',
                        type: 'reference',
                        valueJSX: [{
                            name: 'equal',
                            conditionType: [Conditions.eq],
                            renderInput: (p, onChange) => {
                                return <DropdownInput
                                    {...stateFilterDropdownConfig}
                                    value={p.value.value}
                                    onChange={v => onChange(v)}
                                />
                            }
                        }],
                        resolve: filterData => {
                            let filter: BookingFilterInput = {
                                id: {
                                    gt: 0
                                }
                            };
                            const date = DateUtil.toIsoDate(DateUtil.beginningOfDay())
                            switch (filterData.value as StateFilterTypes) {
                                case "future":
                                    filter = {
                                        inDate: {
                                            gte: date
                                        }
                                    };
                                    break;
                                case "gone":
                                    filter = {
                                        outDate: {
                                            lte: date
                                        }
                                    }
                                    break;
                                case "staingAndFuture":
                                    filter = {
                                        outDate: {
                                            gte: date
                                        }
                                    }
                                    break;
                                case "staing":
                                    filter = {
                                        details: {
                                            some: {
                                                date: {
                                                    eq: date
                                                }
                                            }
                                        }
                                    }
                                    break;
                            }

                            return filter;
                        }
                    }
                }
            ],
            render: {
                actions: {
                    mutationPermissionRole: Roles.ACCOMMODATION_BOOKINGS_WRITE,
                    viewUrl: AppRoutes.ACCOMMODATION_BOOKING_VIEW,
                    editUrl: AppRoutes.ACCOMMODATION_BOOKING_EDIT
                },
                footer: props.hideTotalRow ? undefined : {
                    table: {
                        bottom: getBottomClassName(props.hasBottomNavigation).table,
                        columns: [
                            {
                                name: 'total',
                                colSpan: 3,
                                className: 'text-right font-bold',
                                value: `${context.t('fields.total.name')}:`
                            },
                            {
                                name: 'nights',
                                value: <span>{footerData.nightsSum} / {footerData.nightsAverage.toFixed(1)}</span>
                            },
                            {
                                name: 'person'
                            },
                            {
                                name: 'guestCount',
                                value: <span>{footerData.guestCountSum} / {footerData.guestCountAverage.toFixed(1)}</span>
                            },
                            {
                                name: 'children-roomType-price',
                                colSpan: 3,
                            },
                            {
                                name: 'bookingTotal',
                                value: <span>{footerData.totalSum} / {footerData.totalAverage.toFixed(1)}</span>
                            },
                            {
                                name: 'state',
                            },
                            {
                                name: 'channelTotal',
                                value: <span>{footerData.channelTotalSum} / {footerData.channelTotalAverage.toFixed(1)}</span>
                            }
                        ]
                    },
                    card: {
                        bottom: getBottomClassName(props.hasBottomNavigation).card,
                        element: <div key={props.name + "-booking-datalist-cart-footer"} className="flex flex-wrap">
                            <div className="px-2">
                                <span><FontAwesomeIcon icon={faMoon} /> </span>
                                <span>{footerData.nightsSum} / {footerData.nightsAverage.toFixed(1)}</span>
                            </div>
                            <div className="px-2">
                                <span><FontAwesomeIcon icon={faUserFriends} /> </span>
                                <span>{footerData.guestCountSum} / {footerData.guestCountAverage.toFixed(1)}</span>
                            </div>
                            <div className="px-2">
                                <span><FontAwesomeIcon icon={faFileInvoiceDollar} /> </span>
                                <span>{footerData.totalSum} / {footerData.totalAverage.toFixed(1)}</span>
                            </div>
                            <div className="px-2">
                                <span><FontAwesomeIcon icon={faBookAtlas} /> </span>
                                <span>{footerData.channelTotalSum} / {footerData.channelTotalAverage.toFixed(1)}</span>
                            </div>
                        </div>
                    }
                }
            }
        }
    }

    const config = lodash.merge({}, datalistDefaultConfig, props.config);

    return <Datalist {...config} />;
};

export default BookingDatalist;
